import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import AdvanceSelectLabel from '../../advance-select/advance-select-label';
import DatepickerBox from '../../datepicker-box/datepicker-box';
import {
  BorderedBoxIcon,
  CloseIcon,
  DocumentDownloadIcon,
  UploadDocumentOutlinedIcon,
  NextIcon,
  PlusIcon,
} from '../../icons/icons';
import TextBoxGroup from '../../text-box-group/text-box-group';
import SwitchGroup from '../../switch-group/switch-group';
import Checkbox from '../../custom-checkbox/custom-checkbox';
import Button from '../../button/button';
// import TitleEditable2 from '../title-editable2';
import TextBox from '../../text-box/text-box';
import {
  SEARCH_CURRENCY,
  QUERY_USER_PLANTS,
} from '../../../graphQL/supplierQuery';
import Action from '../../action/action';
import { getBach, handlePrdPkgChange, isGrossWtInvalid } from './buyerHelper';
import TitleEditable from '../title-editable';
import Pagination from '../../pagination/pagination';
import MultiSearchSelect from '../../search-select/multi-search-select';
import ActionButton from '../../actions/actions-btn';
import Dialog from '../../dialog/dialog';
import useDialog from '../../dialog/useDialog';
import UploadExcelFile from '../../add-order-lines/uploadExcel';
import DialogPanel from '../../dialog-panel/dialog-panel';
import DialogPanelBody from '../../dialog-panel/dialog-panel-body/dialog-panel-body';
import DialogPanelFooter from '../../dialog-panel/dialog-panel-footer/dialog-panel-footer';
import PreviewExcelFile from '../../preview-excel/preview-excelfile';

function ProductDetailsTab({
  setSupplierTab,
  prodPackDetails,
  setProdPackDetails,
  products,
  setProducts,
  setTabDetails,
  packages,
  setPackages,
  productOptions,
  createSingleOrder,
  handleBack,
  handleSave,
  shouldPaginate,
  setExcelData,
  enableSelectHazmat,
  unCodeOptions,
  setUnCodeOptions,
}) {
  const [t] = useTranslation('manageorders');
  const [t2] = useTranslation('toast');
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachment, setAttachment] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [isEditingProductNameValid, setIsEditingProductNameValid] =
    useState(true);
  const [hasUnsavedProductName, setHasUnsavedProductName] = useState(false);
  const perPage = 5;

  const [plantOptions, setPlantOptions] = useState([]);

  const [fetchPlantData] = useLazyQuery(QUERY_USER_PLANTS);

  useEffect(() => {
    fetchPlantData({
      onCompleted: (res) => {
        setPlantOptions(
          res?.fetchUserPlants?.data?.map((item) => ({
            label: item.entityName,
            value: item._id,
            entityName: item.entityName,
            entityType: item.entityType,
          })) || []
        );
      },
    });
  }, []);

  const { isShowing: uploadExcelDocument, toggle: toogleUploadExcelDocument } =
    useDialog();
  const { isShowing: priveExcelFile, toggle: handlepriveExcelFile } =
    useDialog();

  const { loading } = useQuery(SEARCH_CURRENCY, {
    onCompleted: (data) => {
      setCurrencyOptions(
        data?.fetchCurrency?.map((currency) => ({
          label: currency?.CurrencyCode,
          value: currency?.CurrencyCode,
        }))
      );
    },
    fetchPolicy: 'cache-first',
  });
  const validateExcelDataBeforeUpload = (data) => {
    if (!data || !data.length) {
      toast.error(t2('excelupload.isempty'));
      return;
    }
    const expectedHeaders = [
      'Product name',
      'Qty',
      'Length',
      'Width',
      'Height',
      'Gross weight',
      'Price Per Unit',
      'Currency Code',
    ];

    const headerRow = data[0];

    const actualHeaders = Object.keys(headerRow).filter(
      (key) => key !== 'rowNumber'
    );

    const normalize = (arr) => arr.map((h) => h.toLowerCase().trim());

    const normalizedExpected = normalize(expectedHeaders);
    const normalizedActual = normalize(actualHeaders);

    const isHeaderValid =
      normalizedExpected.length === normalizedActual.length &&
      normalizedExpected.every((h) => normalizedActual.includes(h));

    if (!isHeaderValid) {
      toast.error(t2('excelupload.invalidcolumn'));
      return;
    }

    const dataRows = data.slice(1);

    if (dataRows.length > 50) {
      toast.error(t2('excelupload.maxdata'));
      return;
    }

    const hasEmpty = dataRows.some((row) =>
      expectedHeaders.some((key) => !row[key])
    );

    if (hasEmpty) {
      toast.error(t2('excelupload.emptyfields'));
      return;
    }

    const numericFields = [
      'Qty',
      'Length',
      'Width',
      'Height',
      'Gross weight',
      'Price Per Unit',
    ];

    const invalidNumber = dataRows.some((row) =>
      numericFields.some((field) => Number.isNaN(Number(row[field])))
    );

    if (invalidNumber) {
      toast.error(t2('excelupload.invalidvalues'));
      return;
    }
    setExcelData(data);
  };

  const handleAddAnotherProduct = useCallback(() => {
    let newId = 1;
    if (products?.length) {
      newId = products[products.length - 1].id + 1 || 1;
    }
    const newProduct = {
      id: newId,
      name: '',
      itemCode: '',
      quantity: null,
      length: null,
      width: null,
      height: null,
      grossWeight: null,
      pricePerUnit: null,
      currency: 'USD',
      measurementUnit: 'metric',
      customClearance: false,
      hazmat: false,
    };
    setProducts((prev) => [...prev, newProduct]);
    const newTotal = (products?.length || 0) + 1;
    const newVisible = Math.min(newTotal, 15);
    if (createSingleOrder && newVisible > perPage) {
      const newPage = Math.min(3, Math.ceil(newVisible / perPage));
      setCurrentPage(newPage);
    }
  }, [products, setProducts]);

  const currencySelectOptions = useMemo(() => {
    return currencyOptions?.map((val) => (
      <option key={val?.value} value={val?.value}>
        {val?.label}
      </option>
    ));
  }, [currencyOptions]);

  const currencyValues = currencySelectOptions.map((opt) => opt.props.value);

  const visibleProducts = useMemo(() => {
    return (products || []).slice(0, 100);
  }, [products]);

  const pagesCount = Math.max(
    1,
    Math.ceil((visibleProducts?.length || 0) / perPage)
  );
  const paginationDataArray = useMemo(
    () => Array.from({ length: Math.min(pagesCount, 20) }, (_, i) => i + 1),
    [visibleProducts?.length]
  );
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return visibleProducts.slice(start, start + perPage);
  }, [visibleProducts, currentPage]);

  const displayedProducts =
    shouldPaginate && visibleProducts.length > perPage
      ? paginatedProducts
      : visibleProducts;
  const isScrollable = (visibleProducts?.length || 0) > 5;
  const validateProdPackDetails = () => {
    const errors = {};
    // use if required
    if (!prodPackDetails.kindOfGoods) {
      errors.kindOfGoods = 'Kind of Goods is required.';
    }
    if (!prodPackDetails.cargoReadyDate) {
      errors.cargoReadyDate = 'Cargo Ready Date is required.';
    }
    if (!prodPackDetails.plant) {
      errors.plant = 'Plant is required.';
    }
    if (prodPackDetails.customClearance && !prodPackDetails.HSCode?.trim()) {
      errors.HSCode = 'HS Code is required when Custom Clearance is true.';
    }
    if (
      prodPackDetails.hazmat &&
      (!prodPackDetails.UNCode || prodPackDetails.UNCode.length === 0)
    ) {
      errors.UNCode = 'UN Code is required when Hazmat is true.';
    }
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  const isHazmatInvalid =
    prodPackDetails?.hazmat &&
    (!prodPackDetails?.UNCode || prodPackDetails?.UNCode.length === 0);

  const isValidProductName = (value) => {
    const trimmed = value.trim();
    if (!/[a-zA-Z0-9]/.test(trimmed)) return false;
    if (/^[^a-zA-Z0-9]/.test(trimmed)) return false;
    if (/[^a-zA-Z0-9]$/.test(trimmed)) return false;
    return true;
  };

  const validateProducts = () => {
    const isProductValid = (product) => {
      const {
        name,
        quantity,
        length,
        width,
        height,
        grossWeight,
        pricePerUnit,
        currency,
        measurementUnit,
      } = product;
      return (
        // name?.trim() &&
        !!name?.trim() &&
        isValidProductName(name) &&
        quantity &&
        length &&
        width &&
        height &&
        grossWeight &&
        pricePerUnit &&
        currency &&
        measurementUnit
      );
    };

    const isValid = products?.every(isProductValid);
    return {
      isValid,
      errorMessage: isValid
        ? null
        : 'At least one product must have all fields filled.',
    };
  };

  useEffect(() => {
    const isGrossWtValid = packages?.every(
      (pkg) => !isGrossWtInvalid(pkg, products)
    );

    setTabDetails((prev) => {
      const updatedTabDetails = { ...prev };
      if (validateProdPackDetails()?.isValid && validateProducts()?.isValid) {
        updatedTabDetails.disablePkgDetails = false;
      } else {
        updatedTabDetails.disablePkgDetails = true;
        updatedTabDetails.disableShipDetails = true;
        updatedTabDetails.disableNxtShip = true;
      }
      if (!isGrossWtValid) {
        updatedTabDetails.disableShipDetails = true;
        updatedTabDetails.disableNxtShip = true;
      }
      return updatedTabDetails;
    });
  }, [
    validateProdPackDetails()?.isValid,
    validateProducts()?.isValid,
    products,
  ]);

  const deleteProduct = (id) => {
    if (products?.length > 1) {
      setProducts((prev) => {
        const updatedProducts = prev?.filter((prd) => {
          if (prd?.id !== id) {
            return true;
          }
          return false;
        });
        return updatedProducts;
      });

      const visibleLen = Math.min((products?.length || 0) - 1, 100);
      setCurrentPage((cur) => {
        const last = Math.max(1, Math.ceil(visibleLen / perPage));
        return cur > last ? last : cur;
      });
      setPackages((prev) => {
        return prev?.map((pkg) => {
          if (pkg?.selectedProductIds?.includes(id)) {
            const updatedIds = pkg?.selectedProductIds?.filter(
              (prdId) => id !== prdId
            );
            return { ...pkg, selectedProductIds: updatedIds };
          }
          return pkg;
        });
      });
    }
  };

  return (
    <div className="supplier-booking-content ask-qut">
      <h3>{t('manageordersscreen.productdetails.goodsinformation')}</h3>
      <div className="d-flex w-100 align-center">
        {/* <div className="d-flex w-50"> */}
        {!createSingleOrder && (
          <AdvanceSelectLabel
            id="kindOfGoods"
            label={t('manageordersscreen.productdetails.kindofgoods')}
            placeholder={t(
              'manageordersscreen.productdetails.kindofgoodsplaceholder'
            )}
            required
            data={[
              { label: 'General Cargo', value: 'General Cargo' },
              { label: 'Racks', value: 'Racks', disabled: true },
            ]}
            defaultValue={{
              label: prodPackDetails?.kindOfGoods,
              value: prodPackDetails?.kindOfGoods,
            }}
            removeSelectedValue={() => {
              setProdPackDetails((prev) => ({
                ...prev,
                kindOfGoods: '',
              }));
            }}
            className="mr-20 w-100"
            onChange={(e) =>
              setProdPackDetails((prev) => ({
                ...prev,
                kindOfGoods: e?.target?.value,
              }))
            }
          />
        )}
        <DatepickerBox
          required
          label={t('manageordersscreen.productdetails.cargoreadydate')}
          placeholder={t(
            'manageordersscreen.productdetails.cargoreadyplaceholder'
          )}
          setDate={(e) => {
            setProdPackDetails((prev) => {
              return { ...prev, cargoReadyDate: e };
            });
          }}
          selectedDate={prodPackDetails?.cargoReadyDate}
          className={createSingleOrder ? 'w-50' : ''}
        />
        <AdvanceSelectLabel
          label={t('manageordersscreen.productdetails.plant')}
          placeholder={t('manageordersscreen.productdetails.plantplaceholder')}
          required
          data={plantOptions}
          defaultValue={
            prodPackDetails?.plant
              ? {
                  label: prodPackDetails?.plant?.entityName,
                  value: prodPackDetails?.plant?._id,
                }
              : {}
          }
          onChange={(e) => {
            const selected = plantOptions?.find(
              (opt) => String(opt.value) === String(e?.target?.value)
            );
            setProdPackDetails((prev) => ({
              ...prev,
              plant: selected
                ? {
                    _id: selected.value,
                    entityName: selected.entityName,
                    entityType: selected.entityType,
                  }
                : null,
            }));
          }}
          removeSelectedValue={() => {
            setProdPackDetails((prev) => ({ ...prev, plant: null }));
          }}
          className="ml-20 mr-20 w-100"
        />
        <div className="d-flex w-100 align-center justify-end ml-auto">
          <ActionButton className="btn-blue-bg" type="button">
            <DocumentDownloadIcon color="#ffffff" />
            {t('manageordersscreen.productdetails.downloadtemplate')}
          </ActionButton>
          <ActionButton
            className="btn-blue-bg"
            type="button"
            onClick={toogleUploadExcelDocument}
          >
            <UploadDocumentOutlinedIcon />
            {t('manageordersscreen.bookingdetailsbtn.uploaddoc')}
          </ActionButton>
        </div>
      </div>

      <div className="package-details-outerwrapper booking">
        <div className="package-details-header">
          <div className="d-flex justify-between">
            <h3 className="m-0">
              {t('manageordersscreen.productdetails.createproducts')}
            </h3>
            {false && (
              <span className="card-icon">
                <BorderedBoxIcon />
              </span>
            )}
          </div>
          {!createSingleOrder && (
            <div className="d-flex cc-and-hazmat-row">
              {false && (
                <>
                  <Checkbox
                    name="customClearance"
                    value="Custom Clearance"
                    label={t(
                      'manageordersscreen.productdetails.customclearance'
                    )}
                    onChange={(e) => {
                      setProdPackDetails((prev) => ({
                        ...prev,
                        customClearance: e?.target?.checked,
                      }));
                    }}
                    checked={prodPackDetails?.customClearance}
                  />

                  {prodPackDetails?.customClearance && (
                    <input
                      id="hsCode"
                      name="HSCode"
                      type="text"
                      placeholder={`${t(
                        'manageordersscreen.productdetails.hscodeplaceholder'
                      )}*`}
                      onChange={({ target: { value } }) => {
                        // Allow only numeric characters and trim to 10 characters
                        if (/^\d*$/.test(value) && value.length <= 10) {
                          setProdPackDetails((prev) => ({
                            ...prev,
                            HSCode: value,
                          }));
                        }
                      }}
                      value={prodPackDetails?.HSCode}
                      pattern="\d{1,10}"
                      required
                    />
                  )}
                </>
              )}

              <Checkbox
                name="hazmat"
                value="HAZMAT"
                label={t('manageordersscreen.productdetails.hazmat')}
                onChange={(e) => {
                  setProdPackDetails((prev) => ({
                    ...prev,
                    hazmat: e?.target?.checked,
                  }));
                }}
                checked={prodPackDetails?.hazmat}
              />
              {prodPackDetails?.hazmat &&
                (enableSelectHazmat ? (
                  <MultiSearchSelect
                    classNames="auto"
                    enableLabel
                    id="unCode"
                    name="UNCode"
                    data={unCodeOptions}
                    onDataChange={setUnCodeOptions}
                    placeholder={t(
                      'manageordersscreen.productdetails.uncodeplaceholder'
                    )}
                    searchPlaceholder={t(
                      'manageordersscreen.productdetails.AddNewUNCode'
                    )}
                    containerClassName="mb-0 w-200-px hazmat"
                    showFooterMessage={false}
                    // data={currencyOptions}
                    onChange={({ target: { value } }) => {
                      setProdPackDetails((prev) => ({
                        ...prev,
                        UNCode: value,
                      }));
                    }}
                    value={prodPackDetails?.UNCode || []}
                    enableRemove
                  />
                ) : (
                  <input
                    type="text"
                    id="unCode"
                    name="UNCode"
                    placeholder={t(
                      'manageordersscreen.productdetails.uncodeplaceholder'
                    )}
                    onChange={({ target: { value } }) => {
                      // Allow only alphanumeric characters and trim to 10 characters
                      setProdPackDetails((prev) => ({
                        ...prev,
                        UNCode: value,
                      }));
                    }}
                    value={prodPackDetails?.UNCode}
                    pattern="[a-zA-Z0-9]{1,10}"
                    required
                  />
                ))}
            </div>
          )}
        </div>
        <div
          className="package-details-body"
          style={
            isScrollable
              ? { maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }
              : undefined
          }
        >
          {displayedProducts?.map((product) => (
            <div className="package-details-wrapper" key={product?.id}>
              <div className="package-details-card multiple">
                <div className="heading">
                  <div className="left">
                    <span className="card-icon">
                      <BorderedBoxIcon />
                    </span>
                    {productOptions.length === 0 ? (
                      <TitleEditable
                        name="name"
                        value={product?.name}
                        className="mb-0"
                        onChange={(e) => {
                          e.target.name = 'name';
                          handlePrdPkgChange(e, product?.id, setProducts);
                        }}
                        placeholder={t(
                          'manageordersscreen.productdetails.productname'
                        )}
                        errorText={t(
                          'manageordersscreen.productdetails.noproducterror'
                        )}
                        isValidProductName={isValidProductName}
                        isEditingValid={setIsEditingProductNameValid}
                        setHasUnsavedProductName={setHasUnsavedProductName}
                      />
                    ) : (
                      <AdvanceSelectLabel
                        id="product"
                        // label="Product"
                        placeholder={t(
                          'manageordersscreen.productdetails.productplaceholder'
                        )}
                        required
                        data={productOptions}
                        defaultValue={
                          product?.itemCode
                            ? {
                                label: product?.itemCode,
                                value: product?.itemCode,
                              }
                            : null
                        }
                        onChange={(e) => {
                          const value = e?.target?.value;
                          const selected = productOptions.find(
                            (opt) => opt.value === value
                          );
                          setProducts((prev) =>
                            prev.map((item) =>
                              item.id === product.id
                                ? {
                                    ...item,
                                    itemCode: value,
                                    name: selected?.label || value,
                                  }
                                : item
                            )
                          );
                        }}
                        removeSelectedValue={() => {
                          handlePrdPkgChange(
                            {
                              target: {
                                name: 'itemCode',
                                value: '',
                              },
                            },
                            product?.id,
                            setProducts
                          );
                        }}
                        className="w-200-px mb-0"
                        textboxClass="single-order"
                      />
                    )}
                    {/* <input
                    name="name"
                    value={product?.name}
                    className="mb-0"
                    onChange={(e) => {
                      e.target.name = 'name';
                      handlePrdPkgChange(e, product?.id, setProducts);
                    }}
                    placeholder={t(
                      'manageordersscreen.productdetails.productname'
                    )}
                    errorText={t(
                      'manageordersscreen.productdetails.noproducterror'
                    )}
                  /> */}
                    <SwitchGroup
                      name="measurementUnit"
                      defaultSelected={0}
                      onChange={(e) => {
                        handlePrdPkgChange(e, product?.id, setProducts);
                      }}
                      disabled={!(product?.itemCode || product?.name)}
                    >
                      <span
                        className={
                          product?.measurementUnit === 'metric'
                            ? ''
                            : 'inactive'
                        }
                      >
                        {t('manageordersscreen.productdetails.metric')}
                      </span>
                      <span
                        className={
                          product?.measurementUnit === 'imperial'
                            ? ''
                            : 'inactive'
                        }
                      >
                        {t('manageordersscreen.productdetails.imperial')}
                      </span>
                    </SwitchGroup>
                  </div>
                  {products.length > 1 && (
                    <div className="right">
                      <Button
                        styles="icon"
                        icon={<CloseIcon size="1.389vw" />}
                        onClick={() => deleteProduct(product?.id)}
                      />
                    </div>
                  )}
                </div>
                <div className="body">
                  <table>
                    <thead>
                      <tr>
                        {[
                          'quantity',
                          'length',
                          'width',
                          'height',
                          'grossWeight',
                          'pricePerUnit',
                        ]?.map((field) => (
                          <th>
                            {t(`manageordersscreen.productdetails.${field}`)}
                            <span className="required">*</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {[
                          'quantity',
                          'length',
                          'width',
                          'height',
                          'grossWeight',
                        ]?.map((field) => (
                          <td key={field}>
                            <TextBoxGroup
                              id={field}
                              name={field}
                              type="number"
                              placeholder={t(
                                `manageordersscreen.productdetails.${field}`
                              )}
                              bach={getBach(product, field)}
                              onChange={(e) =>
                                handlePrdPkgChange(e, product?.id, setProducts)
                              }
                              // to maintain validation on pasting text
                              onPaste={(e) => {
                                e.preventDefault(); // Prevent the default paste action
                                const pastedValue =
                                  e.clipboardData.getData('text');
                                if (/^\d*\.?\d*$/.test(pastedValue)) {
                                  const [integerPart, decimalPart] =
                                    pastedValue.split('.');
                                  const sanitizedValue = decimalPart
                                    ? `${integerPart}.${decimalPart.slice(
                                        0,
                                        field === 'quantity' ? 3 : 2
                                      )}`
                                    : integerPart;
                                  handlePrdPkgChange(
                                    {
                                      target: {
                                        name: field,
                                        value: sanitizedValue,
                                      },
                                    },
                                    product.id,
                                    setProducts
                                  );
                                }
                              }}
                              disabled={!(product?.itemCode || product?.name)}
                              value={product?.[field] || ''}
                              decimalLimit={field === 'quantity' ? 3 : 2}
                            />
                          </td>
                        ))}

                        <td>
                          {false && (
                            <Action
                              name="pricePerUnit"
                              label="Price Per Unit"
                              className="select-box"
                            >
                              <Checkbox value="bsjdnf" label="sbcnn" checked />
                            </Action>
                          )}
                          <div className="d-flex align-start price-group w-150">
                            <TextBox
                              id="pricePerUnit"
                              name="pricePerUnit"
                              // type="number"
                              min={0}
                              required
                              placeholder={t(
                                'manageordersscreen.productdetails.priceplaceholder'
                              )}
                              className="quotation"
                              value={product?.pricePerUnit || ''}
                              onChange={(e) =>
                                handlePrdPkgChange(e, product?.id, setProducts)
                              }
                              disabled={!(product?.itemCode || product?.name)}
                              // to maintain validation on pasting text
                              onPaste={(e) => {
                                e.preventDefault(); // Prevent the default paste action
                                const pastedValue =
                                  e.clipboardData.getData('text');
                                if (/^\d*\.?\d*$/.test(pastedValue)) {
                                  const [integerPart, decimalPart] =
                                    pastedValue.split('.');
                                  const sanitizedValue = decimalPart
                                    ? `${integerPart}.${decimalPart.slice(
                                        0,
                                        4
                                      )}`
                                    : integerPart;
                                  handlePrdPkgChange(
                                    {
                                      target: {
                                        name: 'pricePerUnit',
                                        value: sanitizedValue,
                                      },
                                    },
                                    product.id,
                                    setProducts
                                  );
                                }
                              }}
                            />
                            {!loading && (
                              <select
                                className="mt-0"
                                name="currency"
                                onChange={(e) =>
                                  handlePrdPkgChange(
                                    e,
                                    product?.id,
                                    setProducts
                                  )
                                }
                                value={
                                  currencyValues.includes(product?.currency)
                                    ? product.currency
                                    : 'USD'
                                }
                              >
                                {currencySelectOptions}
                              </select>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="supplier-booking-button-area justify-between">
          <button
            type="button"
            className="add-more"
            onClick={handleAddAnotherProduct}
            disabled={products?.length >= 100}
          >
            <PlusIcon color="#0088fa" />{' '}
            {t('manageordersscreen.productdetails.addanotherproduct')}
          </button>
        </div>
      </div>

      <div className="supplier-booking-button-area mt-10 d-block text-right">
        {shouldPaginate && visibleProducts.length > perPage && (
          <Pagination
            paginationData={paginationDataArray}
            currentPage={currentPage}
            totalData={visibleProducts.length}
            perPage={perPage}
            className="no-shadow"
            swicthPage={(p) => setCurrentPage(p)}
          />
        )}
        {createSingleOrder && (
          <Button
            label={t('manageordersscreen.createSingleOrder.back')}
            styles="back"
            size="large"
            spacing="0.694vw"
            className="px-24"
            onClick={handleBack}
          />
        )}
        <Button
          label={
            createSingleOrder
              ? t('manageordersscreen.createSingleOrder.save')
              : t('manageordersscreen.createSingleOrder.next')
          }
          styles="submit"
          size="large"
          iconRight={createSingleOrder ? null : <NextIcon />}
          onClick={() => {
            if (createSingleOrder) {
              handleSave();
            } else {
              setTabDetails((prev) => {
                const updatedTabDetails = { ...prev };
                updatedTabDetails.productDetails = true;
                return updatedTabDetails;
              });
              setSupplierTab('packaging details');
            }
          }}
          spacing="0.694vw"
          className="px-24"
          disabled={
            !validateProdPackDetails()?.isValid ||
            !validateProducts()?.isValid ||
            isHazmatInvalid ||
            !isEditingProductNameValid ||
            hasUnsavedProductName
          }
        />
      </div>
      <Dialog
        isShowing={uploadExcelDocument}
        hide={toogleUploadExcelDocument}
        placement="center"
        maskZindex={1042}
      >
        <DialogPanel
          title={t('manageordersscreen.productdetails.Uploadexceldialog.title')}
        >
          <DialogPanelBody>
            <div className="doc-con">
              <UploadExcelFile
                selectedFiles={selectedFiles}
                setSelectedFiles={(files) => {
                  setSelectedFiles(files);
                  setExcelFile(files?.[0]);
                  // setAddDocuments(true);
                }}
                attachment={attachment}
                onUpload={(data) => {
                  setSelectedFiles(data);
                  setAttachment(data);
                }}
              />
            </div>
          </DialogPanelBody>
          <DialogPanelFooter>
            <Button
              color="cancel"
              label={t(
                'manageordersscreen.productdetails.Uploadexceldialog.clear'
              )}
              onClick={() => {
                setAttachment([]);
                setSelectedFiles([]);
                setExcelFile([]);
              }}
            />
            <Button
              color="primary"
              label={
                attachment?.length
                  ? 'Preview'
                  : t(
                      'manageordersscreen.productdetails.Uploadexceldialog.upload'
                    )
              }
              disabled={!attachment?.length}
              onClick={() => {
                setExcelFile(selectedFiles?.[0]);
                handlepriveExcelFile();
              }}
            />
          </DialogPanelFooter>
        </DialogPanel>
      </Dialog>
      <Dialog
        isShowing={priveExcelFile}
        hide={handlepriveExcelFile}
        placement="center"
        maskZindex={10000001}
      >
        <PreviewExcelFile
          file={excelFile}
          setExcelData={(data) => {
            handlepriveExcelFile();
            toogleUploadExcelDocument();
            validateExcelDataBeforeUpload(data);
            setAttachment([]);
            setSelectedFiles([]);
            setExcelFile([]);
          }}
        />
      </Dialog>
    </div>
  );
}

export default React.memo(ProductDetailsTab);

ProductDetailsTab.propTypes = {
  setSupplierTab: PropTypes.func,
  prodPackDetails: PropTypes.instanceOf(Object),
  setProdPackDetails: PropTypes.func,
  products: PropTypes.instanceOf(Array),
  setProducts: PropTypes.func,
  setTabDetails: PropTypes.func,
  packages: PropTypes.instanceOf(Array),
  setPackages: PropTypes.func,
  productOptions: PropTypes.instanceOf(Array),
  createSingleOrder: PropTypes.bool,
  handleBack: PropTypes.func,
  handleSave: PropTypes.func,
  shouldPaginate: PropTypes.bool,
  setExcelData: PropTypes.func,
  enableSelectHazmat: PropTypes.bool,
  unCodeOptions: PropTypes.arrayOf(PropTypes.string),
  setUnCodeOptions: PropTypes.func,
};

ProductDetailsTab.defaultProps = {
  setSupplierTab: () => {},
  prodPackDetails: {},
  setProdPackDetails: () => {},
  products: [],
  setProducts: () => {},
  setTabDetails: () => {},
  packages: [],
  setPackages: () => {},
  productOptions: [],
  createSingleOrder: false,
  handleBack: () => {},
  handleSave: () => {},
  shouldPaginate: false,
  setExcelData: () => {},
  enableSelectHazmat: true,
  unCodeOptions: [],
  setUnCodeOptions: () => {},
};
